import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MiscUtilService } from '@common-services/util/misc-util.service';
import { TableDataSource } from '@dice/app/directives/table-data-source';
import { provideFilterComponent } from '@dice/app/directives/table-filter.directive';
import { provideSortComponent } from '@dice/app/directives/table-sort.directive';
import {
  Column,
  FilterPredicateFn,
  HeaderContent,
  PaginatorOptions,
  RowContent,
  TableColumnFilter,
  TableConfiguration,
  TableFilterInterface,
  TableSortDefinition,
  TableSortInterface,
} from '@dice/app/types/table.types';
import { has as _has, isEmpty as _isEmpty, noop as _noop } from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';

@Component({
  selector: 'table-component',
  templateUrl: './table.component.html',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
      state('expanded', style({ height: '*', display: 'flex' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
  providers: [provideSortComponent(TableComponent), provideFilterComponent(TableComponent)],
  standalone: false,
})
export class TableComponent<T> implements TableSortInterface<T>, TableFilterInterface<T> {
  private miscUtilService = inject(MiscUtilService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(MatSort) sort: MatSort;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() data: T[];

  @Input() columns: Column[];

  @Input() displayedColumnNames: string[];

  @Input() tableDefinition: TableConfiguration;

  @Input() selectedElement: T;

  @Output() selectedElementChange: EventEmitter<T> = new EventEmitter<T>();

  @Output() mouseOverElementChange: EventEmitter<T> = new EventEmitter<T>();

  @Output() sortChanged: EventEmitter<Sort> = new EventEmitter<Sort>();

  @Input() identityComparator: string;

  @Input() selectedRowClass: string;

  @Input() disableFilter: boolean = false;

  @Input() filterPredicate: FilterPredicateFn<T>;

  @Input() tableColumnFilters: TableColumnFilter[];

  @Input() set searchTerm(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
  }

  @Input() defaultSorting: TableSortDefinition;

  @Input() customPaginatorOptions: PaginatorOptions;

  @Input() isPaged: boolean = false;

  @Input() disableSort: boolean;

  @Input() stickyHeader: boolean = false;

  @Input() customDataSource: boolean = false;

  @Input() noDataFoundLabel: string = '-';

  dataSource: MatTableDataSource<T> | TableDataSource<T> = new MatTableDataSource([]);

  private paginatorOptions: PaginatorOptions = {
    backendPagination: false,
    hidePageSize: true,
    pageSize: 15,
    showFirstLastButtons: true,
    pageChangedCallback: _noop,
    pageIndex: 0,
    pageLength: this.dataSource.filteredData.length,
  };

  readonly hasDetailsRow$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  readonly displayedColumnNames$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  readonly searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  private readonly unsubscriber$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.initColumns();
    this.initDetailsRow();
  }

  ngAfterViewInit(): void {
    this.updatePaginator();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { data, customPaginatorOptions, isPaged, columns, displayedColumnNames, customDataSource } = changes;
    if (customDataSource) {
      this.dataSource = new TableDataSource();
      this.updatePaginator();
    }
    if (data) {
      this.dataSource.data = this.data || [];
      this.updatePaginator();
    }
    if (isPaged || customPaginatorOptions) {
      this.updatePaginator();
    }
    if (columns || displayedColumnNames) {
      this.initColumns();
    }
  }

  ngOnDestroy(): void {
    this.unsubscriber$.next();
  }

  private initColumns(): void {
    const names: string[] = this.displayedColumnNames || (this.columns || []).map((column: Column) => column.name);
    this.displayedColumnNames$.next(names);
  }

  private updatePaginator(): void {
    Promise.resolve().then(() => {
      if (this.customPaginatorOptions && !_isEmpty(this.customPaginatorOptions)) {
        this.paginatorOptions = {
          ...this.paginatorOptions,
          ...this.customPaginatorOptions,
        };
      }
      if (this.isPaged && this.paginator) {
        if (!this.paginatorOptions.backendPagination) {
          this.dataSource.paginator = this.paginator;
          this.paginator.length = this.dataSource.filteredData.length;
        } else {
          this.dataSource.paginator = null;
          this.paginator.length = this.paginatorOptions.pageLength;
        }
        this.paginator.pageSize = this.paginatorOptions.pageSize;
        this.paginator.pageIndex = this.paginatorOptions.pageIndex;
        this.paginator.hidePageSize = this.paginatorOptions.hidePageSize;
        this.paginator.pageSizeOptions = this.paginatorOptions.pageSizeOptions;
        this.paginator.showFirstLastButtons = this.paginatorOptions.showFirstLastButtons;
      }
    });
  }

  pageChangedCallback(pageEvent: PageEvent): void {
    if (this.paginatorOptions.pageChangedCallback) {
      this.paginatorOptions.pageChangedCallback(pageEvent);
    }
  }

  private initDetailsRow(): void {
    const hasDetailsRow = _has(this, 'tableDefinition.detailsRow') && !!this.tableDefinition.detailsRow;
    this.hasDetailsRow$.next(hasDetailsRow);
  }

  getRowClass(rowData: T): string {
    if (!_has(this, 'tableDefinition.tableRow.cssClass')) {
      return '';
    }
    const {
      tableRow: { cssClass },
    } = this.tableDefinition;
    return this.getUpdatedCssClasses(cssClass, rowData);
  }

  getColumnCssClass(table: HeaderContent | RowContent, rowData: T): string | string[] {
    if (!_has(table, 'columnCssClass')) {
      return '';
    }
    const { columnCssClass } = table;
    return this.getUpdatedCssClasses(columnCssClass, rowData);
  }

  private getUpdatedCssClasses(cssClassDefinition: string | string[] | ((rowData: T) => string), rowData: T): string {
    if (cssClassDefinition) {
      if (typeof cssClassDefinition === 'string') {
        return cssClassDefinition;
      }
      if (typeof cssClassDefinition === 'function') {
        return cssClassDefinition(rowData);
      }
      if (Array.isArray(cssClassDefinition)) {
        return cssClassDefinition.join(' ');
      }
    }
    return '';
  }

  rowClickHandler(rowDataElement: T): void {
    if (_has(this, 'tableDefinition.tableRow.click')) {
      this.tableDefinition.tableRow.click(rowDataElement);
    } else {
      this.toggleSelectRow(rowDataElement);
    }
  }

  rowDblClickHandler(rowDataElement: T): void {
    if (_has(this, 'tableDefinition.tableRow.dblClick')) {
      this.tableDefinition.tableRow.dblClick(rowDataElement);
    }
  }

  private toggleSelectRow(row: T): void {
    const selectedRow = this.rowIsSelected(row) ? null : row;
    this.selectedElement = selectedRow;
    this.selectedElementChange.emit(this.selectedElement);
  }

  mouseOverHandler(rowDataElement: T): void {
    this.mouseOverElementChange.emit(rowDataElement);
  }

  getSelectedRowClass(row: T): string {
    if (this.rowIsSelected(row) && !!this.selectedRowClass) {
      return this.selectedRowClass;
    }
    return 'table-row';
  }

  rowIsSelected(row: T): boolean {
    return (
      this.selectedElement &&
      this.identityComparator &&
      row[this.identityComparator] === this.selectedElement[this.identityComparator]
    );
  }

  private hasNoData(): boolean {
    return !this.dataSource || !this.dataSource.data || this.dataSource.data.length === 0;
  }

  private isAllDataFilteredOut(): boolean {
    return this.dataSource && this.dataSource.filteredData && this.dataSource.filteredData.length === 0;
  }

  isNoDataDisplayed(): boolean {
    return this.hasNoData() || this.isAllDataFilteredOut();
  }

  sortData(sort: Sort): void {
    if (sort.active || sort.direction !== '') {
      this.sortChanged.emit(sort);
    }
  }

  trackColumnFn = (index: number, obj: T): string => {
    return this.miscUtilService.trackByFn('name', index, obj) as string;
  };
}
